// Registre de contenu de la modale d'offres centralisée.
// Reproduit les 5 gabarits validés en maquette (vitrine de features, hero Max, panneau « Inclus »).
// Les icônes sont des clés résolues vers FontAwesome dans OffersModal.jsx (ICONS).

import { FEATURES } from '../../../const/features';
import { STORY_QUOTA, STORY_DURATION_PREMIUM } from '../../../const/ephemeralStory';
import { WALL_NOTE_DAILY_LIMIT } from '../../../const/wallNotes';

// Durée longue exprimée en HEURES pour la copie (la constante est en secondes) : le libellé suit
// automatiquement si le produit change de palier.
const STORY_LONG_DURATION_HOURS = STORY_DURATION_PREMIUM / 3600;

// ─── NOMS COMMERCIAUX — source unique de l'affichage ──────────────────────────────────────────
// À n'écrire nulle part en dur : toute copie qui nomme une offre passe par ces constantes, sinon
// le produit finit avec plusieurs noms en même temps (c'était le cas : « Premium » dans la modale,
// « Voissa Premium » sur la page certification, « Voissa Max » dans les messages d'erreur API).
// ⚠️ Ce sont des LIBELLÉS, pas des identifiants : les slugs (`voissa-plus` / `voissa-max`), les
// clés de plan, `subscription.isPremium` et les classes CSS ne changent JAMAIS avec eux.
export const PLUS_NAME = 'Voissa Plus';
export const MAX_NAME = 'Voissa Premium';

// Nom court, repris des maquettes. Il sert là où l'offre est déjà identifiée par le contexte :
// le bandeau du haut, le lien vers le détail, et les phrases qui la citent au fil du texte.
// Partout où l'offre est NOMMÉE — le rang d'offre, les boutons — on garde le nom complet.
export const PLUS_SHORT = 'Plus';
export const MAX_SHORT = 'Premium';

// Vitrine Voissa Plus (6 tuiles) — les 5 de la maquette + les stats (argument majeur depuis leur
// passage côté Plus). `cap` = capability associée (pour la tuile active). « Plein écran » reste en
// DERNIER : c'est l'emplacement remplacé par les tuiles hors vitrine (swap ci-dessous).
export const PLUS_VITRINE = [
    { cap: 'canCreateStory', label: 'Stories', icon: 'camera' },
    { cap: 'canCreatePoll', label: 'Sondages', icon: 'chart' },
    { cap: 'canRepost', label: 'Repartage', icon: 'repeat' },
    { cap: 'canReact', label: 'Réactions', icon: 'smile' },
    { cap: 'canViewStats', label: 'Stats', icon: 'stats' },
    { cap: 'canUseFullscreen', label: 'Plein écran', icon: 'expand' },
];

// Triggers qui mettent en avant une tuile EXISTANTE de la vitrine sous une autre capability
// (ex. la lecture des stories highlighte la tuile « Stories », portée par canCreateStory).
export const VITRINE_CAP_ALIAS = {
    canViewStories: 'canCreateStory',
    // Quotas de stories épuisés : même tuile mise en avant que la feature pleine — c'est bien
    // « Stories » qu'on vend, la nuance étant seulement qu'on la vend déplafonnée.
    canCreateStoryDaily: 'canCreateStory',
    canViewStoriesDaily: 'canCreateStory',
    canUseStoryLongDuration: 'canCreateStory', // l'option de durée met en avant la tuile Stories
    canUseStoryCircle: 'canCreateStory',       // idem pour la visibilité réservée au cercle
    canViewStatsMembers: 'canViewStats', // la liste des membres (désormais Plus) met en avant la tuile Stats
    canViewLastVisitors: 'canViewStats', // idem pour les visiteurs : ils vivent dans la page Stats
};

// Features Plus hors vitrine : leur tuile n'apparaît que lorsqu'elles déclenchent la modale —
// elle remplace alors le dernier emplacement (« Plein écran »).
export const PLUS_VITRINE_EXTRA = {
    canUseDarkMode: { cap: 'canUseDarkMode', label: 'Mode sombre', icon: 'moon' },
    canWatchFullVideos: { cap: 'canWatchFullVideos', label: 'Vidéos', icon: 'video' },
    canSaveUnlimitedBookmarks: { cap: 'canSaveUnlimitedBookmarks', label: 'Favoris', icon: 'heart' },
    canViewAllNotifications: { cap: 'canViewAllNotifications', label: 'Notifs', icon: 'bell' },
};

// Liste de la carte Voissa Plus (cases à cocher). Plus = les statistiques COMPLÈTES : agrégées
// (courbes, camemberts, récap, top 3) ET la liste des membres (qui vous a vu / aimé,
// canViewStatsMembers). Les 3 quotas levés : vidéos + favoris + notifications.
// Badge de Voissa Plus : nommé à part pour être retiré de « Et tout ce que contient Voissa Plus »
// côté Premium, qui a déjà le badge gold — afficher les deux se contredisait (Rudy, 05/10).
export const PLUS_BADGE_FEATURE = 'Profil certifié argent';
// Petits mots : plafonnés en Voissa Plus, illimités en Premium. Nommé à part pour la même raison
// que le badge : côté Premium, « 5 par jour » contredirait « Petits mots illimités » (Bertrand, 21/10).
export const PLUS_WALL_NOTE_FEATURE = `${WALL_NOTE_DAILY_LIMIT.PLUS} petits mots par jour`;
export const MAX_WALL_NOTE_FEATURE = 'Petits mots illimités';
// Petits mots et réponses aux stories ajoutés APRÈS les cinq premières lignes : l'onglet Voissa
// Plus n'en affiche que cinq avant son dépliant (PLUS_TAB_VISIBLE), elles restent inchangées.
export const PLUS_CARD_FEATURES = ['Stories éphémères', 'Sondages', 'Repartage & réactions', 'Statistiques complètes (dont la liste des membres)', 'Vidéos en plein écran, sans limite', 'Favoris et notifications illimités', PLUS_WALL_NOTE_FEATURE, 'Réponses aux stories', 'Mode sombre', 'Lien en bio', PLUS_BADGE_FEATURE];

// Le contenu de Voissa Plus tel qu'il figure dans Premium : sans le badge argent (Premium a le
// gold) ni le quota de petits mots (Premium n'en a pas).
export const PLUS_FEATURES_IN_PREMIUM = PLUS_CARD_FEATURES.filter((f) => f !== PLUS_BADGE_FEATURE && f !== PLUS_WALL_NOTE_FEATURE);

// Liste de la carte Voissa Max (comparatif 2 colonnes) : les exclusivités Max = le relationnel
// (Vmessenger, filtre, cercle) + le badge gold. Les statistiques (y compris la liste des membres,
// donc « vos visiteurs ») sont désormais incluses dès Voissa Plus → elles ne figurent plus ici, pour
// ne pas vendre deux fois le même argument. Le chat webcam n'est plus vendu (feature produit
// retirée — la cap canChatWebcam subsiste côté API).
export const MAX_CARD_FEATURES = [
    { icon: 'check', label: 'Tout Voissa Plus', strong: true },
    { icon: 'message', label: 'Vmessenger illimité' },
    { icon: 'filter', label: 'Filtre des membres' },
    { icon: 'heart', label: 'Votre cercle privé' },
    { icon: 'pen', label: MAX_WALL_NOTE_FEATURE },
    // Exclusivité Max : la certification vidéo, qui offre pourtant le reste du relationnel,
    // n'accorde PAS l'incognito (cf. TRIGGER_COPY.canUseIncognito).
    // Retirée tant que le mode est coupé : ne pas vendre ce qui n'est pas activable.
    ...(FEATURES.INCOGNITO ? [{ icon: 'incognito', label: 'Mode incognito' }] : []),
    { icon: 'badge', label: 'Profil certifié gold' },
];

// Panneau « Inclus dans Voissa Max » (gabarit action bloquée Max). `cap` = ligne à mettre en avant.
// Les stats (agrégées, liste des membres ET vues de profil, i.e. « vos visiteurs ») étant désormais
// incluses dès Voissa Plus, elles ne figurent plus parmi les arguments de Premium :
// Max = le relationnel + tout Plus.
export const MAX_PANEL = [
    { icon: 'message', label: 'Vmessenger en illimité', cap: 'canUseMessenger' },
    { icon: 'filter', label: 'Filtre des membres', cap: 'canFilterMembers' },
    ...(FEATURES.INCOGNITO ? [{ icon: 'incognito', label: 'Naviguer en incognito', cap: 'canUseIncognito' }] : []),
    { icon: 'sparkles', label: 'Et tout Voissa Plus inclus' },
];

// --- Gabarit CERTIFICATION (action Max bloquée + profil éligible) -----------------------------
// La certif vidéo accorde EXACTEMENT le relationnel Max et lève les quotas, gratuitement. C'est
// donc elle qu'on vend quand l'utilisateur y est éligible ; Premium ne reste qu'en repli payant.

// Périmètre RÉEL du certifié, relevé dans l'API (voissa-api-rest, pas dans les SPEC-*.md qui
// décrivent l'ancien resolver booléen) :
//   - grant `certified` = RELATIONAL_CAPS + QUOTA_CAPS + `canViewProfileVisitors` (const/grants.js),
//     cette dernière ouvrant la liste de SES visiteurs de profil sans donner le dashboard de stats,
//     + `canCreateStoryDaily` (publier N stories/jour) ;
//   - grant `certified-basic` = `canViewStoriesDaily` (ouvrir N stories/jour), accordé à TOUTE
//     certification validée — hommes inclus, photo incluse, sans péremption.
// Les deux lignes « Stories » ci-dessous décrivent donc un accès PLAFONNÉ, et c'est écrit tel quel :
// promettre les stories sans dire « par jour » produirait une déception à la 2e story.
// NE PAS y ajouter le reste de la création Voissa Plus (sondages, repartage, réactions, plein
// écran, mode sombre) ni le dashboard de stats (canViewStats / canViewStatsMembers) : les grants de
// certification ne les contiennent pas. Deux caps accordées mais volontairement NON vendues ici :
// `canChatWebcam` (feature produit retirée du front) et `canViewLastVisitors` (ne gate plus rien,
// aucun consommateur serveur ni front).
// Dans une phrase, un petit nombre s'écrit en toutes lettres — « deux stories », pas « 2 stories ».
// Le nombre reste tiré de STORY_QUOTA : si le plafond bouge, la phrase suit, et retombe sur le
// chiffre au-delà de la table plutôt que d'afficher un mot faux.
const NOMBRES_EN_LETTRES = ['zéro', 'une', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix'];
const compte = (n, singulier, pluriel = singulier) =>
    `${NOMBRES_EN_LETTRES[n] ?? n} ${n > 1 ? pluriel : singulier}`;
// « petit mot » est masculin : « un petit mot », pas « une ».
const petitsMots = (n) => (n === 1 ? 'un petit mot' : compte(n, 'petit mot', 'petits mots'));

export const CERTIF_PANEL = [
    // `quota` : affiché en pastille sous le libellé, suivi de « illimité en Premium » — la
    // présentation de Bertrand (07/10) dit à la fois ce que la certification donne et ce que
    // Premium ajoute, là où « (1/jour) » laissait croire à une limite sans issue.
    { icon: 'camera', label: 'Publication de story', quota: `${STORY_QUOTA.PUBLISH_PER_DAY} / jour`, cap: 'canCreateStory' },
    { icon: 'eye', label: 'Visualisation des stories', quota: `${STORY_QUOTA.VIEW_PER_DAY} / jour`, cap: 'canViewStories' },
    // Petits mots : la certification en ouvre un par jour. Le palier Voissa Plus (5 / jour) n'est
    // volontairement pas cité : la modale pousse vers Premium (Rudy, 15/10/2026).
    { icon: 'pen', label: 'Petit mot sur le mur des membres', quota: `${WALL_NOTE_DAILY_LIMIT.CERTIFIED} / jour`, cap: 'canWriteWallNote' },
    { icon: 'message', label: 'Vmessenger en illimité', cap: 'canUseMessenger' },
    { icon: 'filter', label: 'Filtre des membres', cap: 'canFilterMembers' },
    { icon: 'users', label: 'Votre cercle privé', cap: 'canAddToCircle' },
    { icon: 'eye', label: 'Qui a visité votre profil' },
    { icon: 'video', label: 'Vidéos, favoris et notifications sans limite' },
];

export const CERTIF_EYEBROW = 'Certification gratuite';
// « Certains avantages » ne disait rien : ni ce que ça coûte en temps, ni combien de temps ça
// dure, ni ce que ça ouvre. La maquette annonce les trois, et la liste juste en dessous répond.
// Deux informations distinctes — ce qu'est la vérification, ce qu'elle ouvre — donc deux lignes.
// En une seule phrase, la coupure tombait au hasard de la largeur disponible.
export const CERTIF_SUBHEADLINE = [
    'Une vérification vidéo, gratuite, valable 12 mois.',
    'Elle débloque tout ce qui suit.',
];
// La durée est désormais dans le sous-titre : la répéter sous le bouton ne servait à rien.
export const CERTIF_NOTE = 'Gratuit · vérification vidéo';

// Accroche par capability déclenchante. Défaut générique si le trigger n'est pas listé.
export const CERTIF_HEADLINE = {
    canUseMessenger: ['Pour discuter en privé, ', 'certifiez votre profil'],
    canFilterMembers: ['Pour filtrer les membres, ', 'certifiez votre profil'],
    canAddToCircle: ['Pour créer votre cercle, ', 'certifiez votre profil'],
    canCreateStory: ['Pour publier une story, ', 'certifiez votre profil'],
    canViewStories: ['Pour regarder les stories, ', 'certifiez votre profil'],
    canWriteWallNote: ['Pour laisser un petit mot, ', 'certifiez votre profil'],
};
export const CERTIF_HEADLINE_DEFAULT = ['Pour débloquer cette fonctionnalité, ', 'certifiez votre profil'];

export const OFFERS = {
    plus: { slug: 'voissa-plus', name: PLUS_NAME },
    max: { slug: 'voissa-max', name: MAX_NAME },
};

// Tête par contexte automatique (lancement / fin d'essai). headline = [pré, mot-clé dégradé, post…].
export const CONTEXT_COPY = {
    launch: {
        // Ni sur-titre ni pseudo (Bertrand, 07/10 : « Bonjour (pseudo) » retiré, en attente de sa
        // confirmation). `anonymous` empêche le pseudo de revenir se coller devant le titre.
        anonymous: true,
        // NE nomme PLUS une offre en particulier : l'écran présente les deux, Premium en premier.
        // L'ancienne accroche annonçait Voissa Plus alors que l'onglet ouvert est Premium — le
        // titre contredisait le contenu, et c'est précisément ce qu'on venait vendre.
        // Pas de sous-titre : il répétait ce que disent les onglets juste en dessous.
        headline: ['Choisissez ', 'votre formule'],
    },
    'trial-end': {
        // Sans pseudo, comme la page de choix (Bertrand, 07/10 : « on retire tout »).
        eyebrow: 'Votre essai est terminé',
        anonymous: true,
        headline: ['Ne perdez pas ', 'vos avantages'],
    },
};

// Accroche contextuelle « action bloquée », par capability déclenchante (copie validée).
// hero = icône du cercle dégradé (gabarit Max) ; lead = ligne à ajouter en tête du panneau Max
// pour les triggers absents du panneau de base (cercle / webcam / stats).
export const TRIGGER_COPY = {
    // --- Voissa Plus (vitrine + essai) ---
    // Stories : `certifiable` depuis l'ouverture des quotas de certification. La certif n'accorde
    // pas la cap PLEINE mais un accès journalier, ce qui suffit à en faire l'alternative gratuite à
    // proposer — et le gabarit certif ne se déclenche que pour les genres éligibles (femmes/couples,
    // cf. certifEligible dans OffersModal), donc les hommes voient bien la vitrine Plus.
    canCreateStory: { tier: 'plus', certifiable: true, eyebrow: `Réservé à ${PLUS_NAME}`, headline: ['Pour publier votre story, activez ', PLUS_NAME], subheadline: 'Stories, vidéos illimitées, mode sombre… tout est débloqué, sans engagement.' },
    canViewStories: { tier: 'plus', certifiable: true, eyebrow: `Réservé à ${PLUS_NAME}`, headline: ['Pour regarder les stories, activez ', PLUS_NAME], subheadline: 'Stories, vidéos illimitées, mode sombre… tout est débloqué, sans engagement.' },
    // Durée 48 h : une OPTION de la publication, pas la publication elle-même. Un membre certifié
    // publie déjà — lui dire « pour publier votre story, activez Voissa Plus » serait faux et
    // décrédibiliserait l'offre. On nomme donc précisément ce qui est réservé. Surtout pas
    // `certifiable` : la certification ouvre la publication, jamais la durée longue.
    canUseStoryLongDuration: { tier: 'plus', eyebrow: `Réservé à ${PLUS_NAME}`, headline: [`Pour garder votre story ${STORY_LONG_DURATION_HOURS} heures, activez `, PLUS_NAME], subheadline: `Vos stories restent visibles deux fois plus longtemps, et débloquez les autres avantages de l'offre ${PLUS_NAME}.` },
    // Visibilité « mon cercle » : comme la durée, c'est une OPTION de la publication. Le membre
    // certifié publie déjà — la copie doit donc parler de la visibilité, pas de la publication.
    // À ne pas confondre avec `canAddToCircle`, qui ouvre le cercle lui-même (Premium/certifié) :
    // ici il s'agit d'y RESTREINDRE une story, ce qui relève des options de création.
    canUseStoryCircle: { tier: 'plus', eyebrow: `Réservé à ${PLUS_NAME}`, headline: ['Pour réserver votre story à votre cercle, activez ', PLUS_NAME], subheadline: `Choisissez qui voit vos stories, et débloquez les autres avantages de l'offre ${PLUS_NAME}.` },
    // --- Quotas de stories ÉPUISÉS (membre déjà certifié) -------------------------------------
    // Troisième état : le droit existe, la journée est consommée. Surtout PAS `certifiable` — le
    // membre EST certifié, lui proposer de se certifier serait absurde. La copie CONSTATE le
    // plafond, sans argumentaire commercial : le membre vient de buter dessus, il veut comprendre
    // pourquoi — les offres sont déjà présentées par les cartes en dessous.
    canCreateStoryDaily: { tier: 'plus', eyebrow: 'Story du jour déjà publiée', headline: ['Pour publier sans limite, activez ', PLUS_NAME], subheadline: `Votre certification vous permet de publier seulement ${compte(STORY_QUOTA.PUBLISH_PER_DAY, 'story', 'stories')} par jour.` },
    canViewStoriesDaily: { tier: 'plus', eyebrow: 'Stories du jour déjà vues', headline: ['Pour regarder sans limite, activez ', PLUS_NAME], subheadline: `Votre certification vous permet de consulter seulement ${compte(STORY_QUOTA.VIEW_PER_DAY, 'story', 'stories')} par jour.` },
    // --- Petits mots (14/10/2026) ----------------------------------------------------------------
    // Droit absent : `certifiable` (la certification ouvre un petit mot par jour aux femmes et aux
    // couples). Quota épuisé : on constate le plafond et on vend l'offre du dessus — Voissa Plus au
    // certifié, Premium (illimité) à l'abonné Voissa Plus. Jamais `certifiable` dans ces deux cas.
    canWriteWallNote: { tier: 'plus', certifiable: true, eyebrow: `Réservé à ${PLUS_NAME}`, headline: ['Pour laisser un petit mot, activez ', PLUS_NAME], subheadline: 'Petits mots, stories, vidéos illimitées, mode sombre… tout est débloqué, sans engagement.' },
    canWriteWallNoteDaily: { tier: 'plus', eyebrow: 'Petit mot du jour déjà envoyé', headline: ['Pour écrire plus de petits mots, activez ', PLUS_NAME], subheadline: `Votre certification vous permet d'écrire ${petitsMots(WALL_NOTE_DAILY_LIMIT.CERTIFIED)} par jour, ${PLUS_NAME} ${petitsMots(WALL_NOTE_DAILY_LIMIT.PLUS)}.` },
    canWriteWallNoteUnlimited: { tier: 'max', hero: 'message', eyebrow: 'Petits mots du jour utilisés', headline: ['Pour écrire sans limite, passez à ', MAX_NAME], subheadline: `${PLUS_NAME} vous permet d'écrire ${petitsMots(WALL_NOTE_DAILY_LIMIT.PLUS)} par jour : avec ${MAX_NAME}, il n'y a plus de limite.` },
    canCreatePoll: { tier: 'plus', eyebrow: `Réservé à ${PLUS_NAME}`, headline: ['Pour créer votre sondage, activez ', PLUS_NAME], subheadline: 'Sondages, stories, vidéos illimitées, mode sombre… tout est débloqué, sans engagement.' },
    canRepost: { tier: 'plus', eyebrow: `Réservé à ${PLUS_NAME}`, headline: ['Pour repartager cette publication, activez ', PLUS_NAME], subheadline: 'Repartage, stories, vidéos illimitées, mode sombre… tout est débloqué, sans engagement.' },
    canReplyStory: { tier: 'plus', eyebrow: `Réservé à ${PLUS_NAME}`, headline: ['Pour répondre à cette story, activez ', PLUS_NAME], subheadline: 'Réponses aux stories, petits mots, vidéos illimitées, mode sombre… tout est débloqué, sans engagement.' },
    canReact: { tier: 'plus', eyebrow: `Réservé à ${PLUS_NAME}`, headline: ['Pour réagir à ce contenu, activez ', PLUS_NAME], subheadline: 'Réactions, stories, vidéos illimitées, mode sombre… tout est débloqué, sans engagement.' },
    canUseFullscreen: { tier: 'plus', eyebrow: `Réservé à ${PLUS_NAME}`, headline: ['Pour passer en plein écran, activez ', PLUS_NAME], subheadline: 'Plein écran, stories, vidéos illimitées, mode sombre… tout est débloqué, sans engagement.' },
    canUseDarkMode: { tier: 'plus', eyebrow: `Réservé à ${PLUS_NAME}`, headline: ['Pour activer le mode sombre, activez ', PLUS_NAME], subheadline: 'Mode sombre, stories, vidéos illimitées… tout est débloqué, sans engagement.' },
    // --- Quotas free — `certifiable` : levés par Voissa Plus OU par la certification (grants). ---
    canWatchFullVideos: { tier: 'plus', certifiable: true, eyebrow: 'Vidéo limitée à 15 secondes', headline: ['Pour regarder la vidéo en entier, activez ', PLUS_NAME], subheadline: 'Vidéos anciennes sans limite, stories, mode sombre… tout est débloqué, sans engagement.' },
    canSaveUnlimitedBookmarks: { tier: 'plus', certifiable: true, eyebrow: 'Limite de favoris atteinte', headline: ['Pour des favoris illimités, activez ', PLUS_NAME], subheadline: 'Favoris illimités, stories, vidéos illimitées, mode sombre… tout est débloqué, sans engagement.' },
    canViewAllNotifications: { tier: 'plus', certifiable: true, eyebrow: 'Notifications limitées', headline: ['Pour voir toutes vos notifications, activez ', PLUS_NAME], subheadline: 'Toutes vos notifications, stories, vidéos illimitées, mode sombre… tout est débloqué, sans engagement.' },
    // --- Voissa Max (hero + panneau « Inclus ») — `certifiable` : la CERTIFICATION vidéo
    // (femmes/couples) accorde aussi la capability => alternative gratuite proposée. ---
    canUseMessenger: { tier: 'max', certifiable: true, hero: 'message', eyebrow: `Réservé à ${MAX_NAME}`, headline: ['Pour discuter en privé, passez à ', MAX_NAME], subheadline: `Vmessenger illimité, tous vos visiteurs, vos statistiques et tous les avantages ${MAX_NAME}.` },
    canFilterMembers: { tier: 'max', certifiable: true, hero: 'filter', eyebrow: `Réservé à ${MAX_NAME}`, headline: ['Pour filtrer les membres, passez à ', MAX_NAME], subheadline: `Filtre des membres, Vmessenger illimité et tous les avantages ${MAX_NAME}.` },
    canAddToCircle: { tier: 'max', certifiable: true, hero: 'users', lead: { icon: 'users', label: 'Votre cercle privé' }, eyebrow: `Réservé à ${MAX_NAME}`, headline: ['Pour créer votre cercle, passez à ', MAX_NAME], subheadline: `Votre cercle privé, Vmessenger illimité et tous les avantages ${MAX_NAME}.` },
    // Mode incognito : Max/Admin UNIQUEMENT — surtout PAS `certifiable`, contrairement au reste du
    // relationnel Max. Le grant `certified` ne porte pas canUseIncognito (cf. const/grants.js côté
    // API) : proposer la certification gratuite comme alternative serait une promesse fausse.
    // Pas de `lead` ici : « Naviguer en incognito » figure DÉJÀ dans MAX_PANEL et sera mis en avant
    // via son `cap`. Ajouter un lead « Mode incognito » créait un doublon.
    canUseIncognito: { tier: 'max', hero: 'incognito', eyebrow: `Réservé à ${MAX_NAME}`, headline: ['Pour naviguer en incognito, passez à ', MAX_NAME], subheadline: `Visitez profils et publications sans laisser votre nom, Vmessenger illimité et tous les avantages ${MAX_NAME}.` },
    // tier plus => gabarit vitrine : hero/lead (spécifiques au gabarit Max) inutiles ici.
    canViewStats: { tier: 'plus', eyebrow: `Réservé à ${PLUS_NAME}`, headline: ['Pour voir vos statistiques, activez ', PLUS_NAME], subheadline: 'Vos statistiques, stories, vidéos illimitées, mode sombre… tout est débloqué, sans engagement.' },
    // Liste des membres désormais incluse dès Voissa Plus → on pitche Plus (gabarit vitrine, la tuile
    // Stats est mise en avant via VITRINE_CAP_ALIAS). hero/lead inutiles pour un tier 'plus'.
    canViewStatsMembers: { tier: 'plus', eyebrow: `Réservé à ${PLUS_NAME}`, headline: ['Pour voir qui a vu et aimé vos contenus, activez ', PLUS_NAME], subheadline: 'La liste des membres, stories, vidéos illimitées, mode sombre… tout est débloqué, sans engagement.' },
    // Les visiteurs (vues de profil) vivent dans la page Stats, donc accessibles dès Voissa Plus :
    // ce n'est plus un argument Premium. La cap canViewLastVisitors subsiste côté API mais ne gate
    // plus rien (la règle VIEW_LAST_VISITORS n'est plus émise) — on garde l'entrée par sécurité.
    canViewLastVisitors: { tier: 'plus', eyebrow: `Réservé à ${PLUS_NAME}`, headline: ['Pour voir tous vos visiteurs, activez ', PLUS_NAME], subheadline: 'Vos visiteurs, stories, vidéos illimitées, mode sombre… tout est débloqué, sans engagement.' },
};

// --- Gabarit BLOCAGE VOISSA PLUS — présentation « Premium d'abord » --------------------------
// Refonte 09/2026 (maquettes Bertrand) : sur une action réservée à Voissa Plus, on présente
// d'abord Voissa Premium (recommandé), puis Voissa Plus en dessous AVEC ses limites. L'objectif
// est commercial : Premium inclut Plus, autant le proposer là où l'envie d'achat naît. La
// fonctionnalité demandée ouvre les DEUX listes — c'est ce qui rend la mise en avant honnête :
// le membre voit que Plus suffit pour ce qu'il voulait faire, et ce qu'il gagne en plus avec
// Premium. Sans cette ligne commune, la modale ressemblerait à une vente forcée.
//
// `kicker` = le verbe exact de l'action tentée. `title` = le bénéfice (5 à 8 mots, il se suffit
// à lui-même : ce gabarit n'affiche PAS de sous-titre). `feature` = le nom de la fonctionnalité,
// repris à l'identique dans les deux offres, sauf si `featureMax` / `featurePlus` le précisent pour
// une offre (petits mots : illimités en Premium, 5 par jour en Voissa Plus).
export const PLUS_BLOCK_COPY = {
    canCreateStory: { kicker: 'Publier une story', title: 'Regardez et publiez des stories éphémères', feature: 'Publier et voir les stories' },
    canViewStories: { kicker: 'Regarder une story', title: 'Regardez et publiez des stories éphémères', feature: 'Publier et voir les stories' },
    // Quotas de certification épuisés : ce n'est plus le droit qui manque, c'est le plafond. Le
    // kicker garde le verbe de l'action, le bénéfice vend le déplafonnement.
    canCreateStoryDaily: { kicker: 'Publier une story', title: 'Publiez vos stories sans compter', feature: 'Stories en illimité' },
    canViewStoriesDaily: { kicker: 'Regarder une story', title: 'Regardez les stories sans compter', feature: 'Stories en illimité' },
    canUseStoryLongDuration: { kicker: 'Prolonger ma story', title: `Vos stories visibles ${STORY_LONG_DURATION_HOURS} heures`, feature: `Stories de ${STORY_LONG_DURATION_HOURS} heures` },
    canUseStoryCircle: { kicker: 'Réserver ma story', title: 'Choisissez qui voit vos stories', feature: 'Stories réservées à votre cercle' },
    // Petits mots : droit absent (« S'abonner »), puis quota épuisé d'un certifié (« Découvrir Plus »).
    // Libellés par offre : Vincent (Premium, « Petits mots illimités ») et Bertrand (Voissa Plus), 19/10.
    canWriteWallNote: { kicker: 'Laisser un petit mot', title: 'Laissez des petits mots sur les murs des membres', feature: 'Petits mots sur les murs', featureMax: 'Petits mots illimités', featurePlus: `${WALL_NOTE_DAILY_LIMIT.PLUS} petits mots par jour sur les murs` },
    canWriteWallNoteDaily: { kicker: 'Laisser un petit mot', title: 'Écrivez plus de petits mots chaque jour', feature: 'Plus de petits mots chaque jour', featureMax: 'Petits mots illimités', featurePlus: `${WALL_NOTE_DAILY_LIMIT.PLUS} petits mots par jour sur les murs` },
    canCreatePoll: { kicker: 'Créer un sondage', title: 'Posez vos questions à votre communauté', feature: 'Créer et publier des sondages' },
    canRepost: { kicker: 'Repartager une publication', title: 'Faites découvrir les contenus qui vous plaisent', feature: 'Repartage des publications' },
    canReplyStory: { kicker: 'Répondre à une story', title: 'Répondez en privé aux stories des membres', feature: 'Réponses aux stories' },
    canReact: { kicker: 'Réagir à une publication', title: "Donnez plus d'émotion à vos j'aime", feature: 'Réactions sur les publications' },
    canUseFullscreen: { kicker: 'Lire en plein écran', title: 'Profitez des vidéos en plein écran, sans limite', feature: 'Lecteur plein écran' },
    canUseDarkMode: { kicker: 'Activer le mode sombre', title: 'Pour reposer vos yeux le soir', feature: 'Mode sombre' },
    canWatchFullVideos: { kicker: 'Regarder la vidéo en entier', title: "Vos vidéos ne s'arrêtent plus à 15 secondes", feature: 'Vidéos de plus de 15 secondes' },
    canSaveUnlimitedBookmarks: { kicker: 'Ajouter aux favoris', title: 'Retrouvez les médias que vous préférez', feature: 'Favoris illimités' },
    canViewAllNotifications: { kicker: 'Activer cette notification', title: 'Ne manquez plus rien de ce qui vous concerne', feature: 'Notifications illimitées' },
    canViewStats: { kicker: 'Voir mes statistiques', title: 'Le détail complet de votre audience', feature: 'Tableau de bord complet' },
    canViewStatsMembers: { kicker: 'Voir qui a vu mes contenus', title: 'Sachez qui vous regarde et qui vous aime', feature: 'La liste des membres' },
    canViewLastVisitors: { kicker: 'Voir mes visiteurs', title: 'Sachez qui passe sur votre profil', feature: 'Vos visiteurs de profil' },
};

// Repli pour un trigger Plus sans entrée dédiée : la modale reste affichable, avec le nom de
// l'offre en bénéfice. Ne JAMAIS laisser le gabarit sans titre — il n'a pas de sous-titre pour
// rattraper.
export const PLUS_BLOCK_FALLBACK = { kicker: `Réservé à ${PLUS_NAME}`, title: `Débloquez tout ${PLUS_NAME}`, feature: `Les fonctionnalités ${PLUS_NAME}` };

// Arguments Premium listés sous la fonctionnalité demandée. DÉRIVÉS de MAX_CARD_FEATURES : les
// avantages de l'offre ne se réécrivent pas gabarit par gabarit, sinon le même produit finit
// décrit de trois façons différentes selon l'écran. La maquette proposait d'autres libellés
// (« Filtres, visio, mode incognito, profil Or ») — ils ne sont pas repris : la visio n'est plus
// vendue et « profil Or » n'est pas le nom du badge chez nous.
// « Tout Voissa Plus » ouvre la liste de la carte comparative ; ici il la FERME, comme dans la
// maquette : la fonctionnalité demandée doit rester la première ligne.
// La fonctionnalité demandée est retirée : elle est déjà la première ligne (« Petits mots
// illimités » sur les modales des petits mots).
export const premiumBlockLines = (feature) => [
    ...MAX_CARD_FEATURES.filter((f) => !f.strong && f.label !== feature).map((f) => f.label),
    `Tout ${PLUS_NAME} inclus`,
];

// Ce que Voissa Plus ne contient PAS. C'est l'argument de bascule vers Premium : il doit rester
// factuel, jamais dévalorisant — le membre peut très bien choisir Plus, et ce serait un bon choix.
// Mêmes mots que les avantages Premium ci-dessus, à la forme négative : une exclusion qui ne
// reprend pas le libellé de ce qu'elle exclut ne se relie à rien dans la tête du lecteur.
export const PLUS_BLOCK_EXCLUSIONS = [
    'Pas de Vmessenger illimité',
    FEATURES.INCOGNITO
        ? 'Ni filtre des membres, ni cercle privé, ni incognito'
        : 'Ni filtre des membres, ni cercle privé',
];

// « Lien en bio » (cas 10 des maquettes) : inclus dans Voissa Plus et donc dans Premium (cap
// canUseProfileLink, 07/10), il figure dans la liste PLUS_CARD_FEATURES. Pas encore de modale
// dédiée : les paramètres du profil se contentent d'un texte. Si un déclencheur est ajouté, lui
// donner son entrée PLUS_BLOCK_COPY (kicker « Ajouter un lien en bio », titre « Partagez le lien
// qui vous tient à cœur », feature « Lien en bio ») et son trigger dans TRIGGER_COPY.

// Deuxième ligne de la carte Voissa Plus : les autres avantages de l'offre. Composée à la volée
// en RETIRANT la fonctionnalité déjà citée juste au-dessus — sinon la carte se répète (« Sondages »
// en ligne 1 et en ligne 2), ce qui donne l'impression que l'offre est plus mince qu'elle ne l'est.
const PLUS_ARGUMENTS = [
    { cap: 'canCreateStory', label: 'stories' },
    { cap: 'canCreatePoll', label: 'sondages' },
    { cap: 'canReact', label: 'réactions' },
    { cap: 'canViewStats', label: 'statistiques complètes' },
    { cap: 'canUseDarkMode', label: 'mode sombre' },
];

// Fonctionnalités couvertes par chaque ligne de PLUS_CARD_FEATURES (clés canoniques, après
// VITRINE_CAP_ALIAS). Sert au dépliant « Voir tous les avantages Voissa Plus » : il ne doit
// montrer que ce que les lignes visibles n'ont pas déjà annoncé (Rudy, 07/10).
const PLUS_FEATURE_CAPS = {
    'Stories éphémères': ['canCreateStory'],
    'Sondages': ['canCreatePoll'],
    'Repartage & réactions': ['canReact', 'canRepost'],
    'Statistiques complètes (dont la liste des membres)': ['canViewStats', 'canViewLastVisitors'],
    'Vidéos en plein écran, sans limite': ['canUseFullscreen', 'canWatchFullVideos'],
    'Favoris et notifications illimités': ['canSaveUnlimitedBookmarks', 'canViewAllNotifications'],
    [PLUS_WALL_NOTE_FEATURE]: ['canWriteWallNote', 'canWriteWallNoteDaily'],
    'Réponses aux stories': ['canReplyStory'],
    'Mode sombre': ['canUseDarkMode'],
    'Lien en bio': ['canUseProfileLink'],
};

// Avantages Voissa Plus que le bloc n'affiche pas encore : ni la fonctionnalité demandée (ligne
// en gras), ni les trois résumées par `plusOtherArguments`.
export const plusMoreFeatures = (cap) => {
    const key = VITRINE_CAP_ALIAS[cap] || cap;
    const dejaVus = new Set([key, ...PLUS_ARGUMENTS.filter((a) => a.cap !== key).slice(0, 3).map((a) => a.cap)]);
    return PLUS_CARD_FEATURES.filter((label) => !(PLUS_FEATURE_CAPS[label] || []).some((c) => dejaVus.has(c)));
};

export const plusOtherArguments = (cap) => {
    const key = VITRINE_CAP_ALIAS[cap] || cap;
    const labels = PLUS_ARGUMENTS.filter((a) => a.cap !== key).slice(0, 3).map((a) => a.label);
    const phrase = labels.join(', ');
    return phrase.charAt(0).toUpperCase() + phrase.slice(1);
};

// Réassurances du pied de modale. « Mention discrète » est exacte : le relevé porte VA MEDIA
// (cf. ui/components2/Subscription/Subscription.jsx). Ne pas y ajouter de promesse non vérifiée.
// Pied de la PAGE de choix d'offre (écran 02). La maquette y remplace le rappel de résiliation
// par la sécurité du paiement : on est à un clic de payer, c'est ce qui rassure à ce moment-là.
// Icônes de la maquette (🔓 🔒 🔄 💳) traduites en FontAwesome (Rudy, 01/10).
export const OFFERS_REASSURANCE = [
    { icon: 'lockOpen', label: 'Sans engagement' },
    { icon: 'lock', label: 'Paiement 100 % sécurisé' },
    { icon: 'creditCard', label: 'Mention discrète sur le relevé' },
];

// Même réassurance sur toutes les modales (Rudy, 01/10) : « Résiliable » répétait « Sans
// engagement », « Paiement sécurisé » dit quelque chose de plus.
export const BLOCK_REASSURANCE = OFFERS_REASSURANCE;

// --- Gabarit BLOCAGE PREMIUM (écran 04 des maquettes) ----------------------------------------
// Une action réservée à Premium n'a pas d'alternative : Voissa Plus ne la contient pas. La modale
// ne compare donc rien, elle explique ce qui est bloqué et ce que Premium ouvre.
//
// `headline` = le bénéfice en deux temps (la seconde partie passe en dégradé), `lead` = la
// promesse en une phrase, `feature` + `featureNote` = la fonction bloquée, mise en tête de liste
// avec sa précision, `cta` = le verbe de l'action qu'on débloque (jamais « S'abonner » : le
// membre veut la fonction, pas l'abonnement).
export const MAX_BLOCK_COPY = {
    canUseMessenger: {
        headline: ['Débloquez Vmessenger', 'et parlez à qui vous voulez'],
        // Pas d'accroche (Bertrand, 07/10) : « Léa vous a répondu » inventait une situation.
        feature: 'Vmessenger illimité',
        featureNote: 'Écrivez, répondez et envoyez des médias à qui vous voulez.',
        cta: 'Débloquer Vmessenger',
    },
    canFilterMembers: {
        headline: ['Trouvez exactement', 'les membres que vous cherchez'],
        lead: 'Débloquez les filtres avancés avec Voissa Premium.',
        feature: 'Filtre des membres',
        featureNote: 'Genre, âge, distance, en ligne, certifiés…',
        cta: 'Débloquer les filtres',
    },
    canAddToCircle: {
        headline: ['Partagez vos contenus', 'avec ceux que vous choisissez'],
        lead: 'Votre contenu. Votre cercle. Vos règles.',
        feature: 'Votre cercle privé',
        featureNote: 'Vous décidez qui voit quoi',
        // « Débloquer » comme les autres vitrines Premium (Rudy, 01/10) ; la maquette disait « Créer mon cercle ».
        cta: 'Débloquer le cercle privé',
    },
    // Petits mots : abonné Voissa Plus qui a utilisé ses 5 petits mots du jour (quota épuisé).
    canWriteWallNoteUnlimited: {
        headline: [`Vos ${WALL_NOTE_DAILY_LIMIT.PLUS} petits mots du jour sont utilisés`, 'écrivez sans limite'],
        lead: `Avec ${MAX_NAME}, les petits mots sont illimités.`,
        feature: 'Petits mots illimités',
        featureNote: 'Sur tous les murs, sans quota du jour',
        cta: `Passer à ${MAX_NAME}`,
    },
    canUseIncognito: {
        headline: ['Profitez de Voissa', 'sans laisser de traces'],
        lead: 'Explorez les profils, les photos et les stories sans être vu.',
        feature: 'Navigation incognito',
        featureNote: 'Vos visites ne laissent plus votre nom',
        // « Débloquer » comme les autres vitrines Premium (Rudy, 01/10) ; la maquette disait « Activer le mode incognito ».
        cta: 'Débloquer le mode incognito',
    },
};

export const MAX_BLOCK_FALLBACK = {
    headline: ['Cette fonctionnalité est réservée', `à ${MAX_NAME}`],
    lead: `${MAX_NAME} débloque tout Voissa, sans limites.`,
    feature: `Les fonctionnalités ${MAX_NAME}`,
    featureNote: null,
    cta: `Passer à ${MAX_NAME}`,
};

// Avantages Premium listés sous la fonction bloquée, puis le reste derrière « Voir tous les
// avantages ». Dérivés de MAX_CARD_FEATURES et PLUS_CARD_FEATURES : une seule source pour tout
// le site. La fonction déclenchante est retirée de la liste, elle est déjà en tête.
// Ces modales ne présentent pas Voissa Plus : le contenu de Plus y est donc détaillé ligne à
// ligne, et non résumé en « Tout Voissa Plus inclus » que le membre ne peut pas déplier (Bertrand, 21/10).
export const maxBlockLines = (feature) => [
    ...MAX_CARD_FEATURES.filter((f) => !f.strong && f.label !== feature).map((f) => f.label),
    ...PLUS_FEATURES_IN_PREMIUM,
];

// --- Vitrines des modales de blocage Premium (écran 04) ---------------------------------------
// Chaque modale montre CE QUI EST BLOQUÉ avant d'en parler : l'activité du chat, les filtres
// qu'on ne peut pas régler, le cercle qu'on ne peut pas composer. Voir la fonction manquante
// convainc mieux que la décrire.
//
// ⚠️ Les volumes du chat (messages, photos, vidéos, membres) sont les ordres de grandeur de la
// maquette, non mesurés ; Bertrand a validé de les garder tels quels (07/10). Pour des chiffres
// réels, il faudrait les faire remonter par l'API de Vmessenger.
export const MAX_SHOWCASE_SAMPLE = {
    // Bloc « Chaque jour sur Vmessenger » de Bertrand (07/10) : les volumes du chat, présentés en
    // bulles de conversation pour qu'on comprenne qu'ils concernent la messagerie et non Voissa.
    // Il remplace la conversation fictive et la « Constellation » : Bertrand ne garde QUE les
    // chiffres. Ordres de grandeur de la maquette, non mesurés (Bertrand : pas besoin des vrais).
    messenger: {
        title: 'Chaque jour sur Vmessenger',
        hero: { value: '170 000+', label: 'messages privés échangés' },
        media: [
            { icon: 'image', value: '10 000+ photos', label: 'envoyées en privé', sent: true },
            { icon: 'play', value: '2 000+ vidéos', label: 'envoyées en privé' },
        ],
        members: { value: '100 000+ membres', label: 'y discutent. Rejoignez-les.' },
    },
    filters: ['Genre', 'Âge', 'Distance', 'En ligne'],
    circle: 'Mon cercle · 6 membres',
};

// Avatars affichés à côté du compteur de membres connectés, dans la publicité du fil.
// ⚠️ DÉCOR : ce sont cinq fois le même avatar par défaut, comme la conversation de « Léa » plus
// haut. Ils illustrent la présence, ils ne représentent personne. À remplacer par de vraies
// photos de membres en ligne si l'équipe le décide — l'appel qui donne le compteur les renvoie
// déjà, il suffirait de lui demander cinq résultats au lieu d'un.
export const FEED_AD_FAKE_AVATARS = Array.from(
    { length: 5 },
    () => `${process.env.PUBLIC_URL}/images/default_avatar.png`,
);

// Type de vitrine par déclencheur. `null` = pas d'illustration (le hero suffit).
export const MAX_SHOWCASE = {
    canUseMessenger: 'conversation',
    canFilterMembers: 'filters',
    canAddToCircle: 'circle',
    canUseIncognito: 'incognito',
};
